import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { PartialId } from '@attain-apps/string-builder'
import _cloneDeep from 'lodash-es/cloneDeep'

import * as state from '../../../../../../../../../state'
import * as display from '../../../../../../display'

import * as components from './components'

/**
 * Dialog for editing DMS records
 */
@ngCore.Component({
  imports: [
    components.entityRecordSelect.Component,
    // NOTE: This addresses circular dependency
    ngCore.forwardRef(() => display.value.Component),
    ds.button.Component,
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
  ],
  selector: 'atn-add-record-dialog',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class AtnAddRecordDialogComponent {
  /** @see {@link state.dms.records.Collection,} */
  public readonly recordsEc = ngCore.inject(state.dms.records.Collection)

  /** @see {@link state.display.Feature} */
  public readonly displayFeature = ngCore.inject(
    state.display.referenceRecords.Feature,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)
  /**
   * Ids of attributes that are non-editable and will not be displayed in the edit dialog.
   */
  // Note: The html is complaining if we don't type this explicitly
  public readonly nonEditableAttributeIds: PartialId[] = [
    ents.core.dms.data.Record.Id.KEY,
  ]

  /** Record being editer */
  private readonly _record = this.displayFeature.selectors.editedRecord.asSignal

  /**
   * Displays values {@link ents.core.dms.data.Record.Values} for the selected record
   */
  public readonly recordValuesSg = ngCore.computed(
    (record = this._record()) => record?.values ?? {},
  )

  /**
   * Displays parentId {@link ents.core.dms.data.Record.parentId} for the selected record
   */
  public readonly recordParentIdSg = ngCore.computed(
    (record = this._record()) => record?.parentId,
  )

  /**
   * List of entity attributes
   */
  public readonly attributesSg = ngCore.computed(
    (entity = this.editedEntitySg()) =>
      entity?.attributes?.map(attribute => ({
        ...attribute,
        id: ents.core.dms.models.Id.Parts.from(attribute)?.partialId,
      })),
  )

  /**
   * Selected {@link state.dms.entities.Entity}
   */
  public readonly editedEntityIdSg =
    this.displayFeature.selectors.editedEntityId.asSignal

  /** Current entity */
  public readonly editedEntitySg = ngCore.computed(
    (editedEntityId = this.editedEntityIdSg()) =>
      this._schemaFeature.selectors.schema.asSignal().getEntity(editedEntityId),
  )
  /** Curently edited entities */
  public readonly editedEntityIdsSg =
    this.displayFeature.selectors.editedEntityIds.asSignal
  /** Current entity's parent */
  public readonly entityParentSg = ngCore.computed(
    (entity = this.editedEntitySg()) =>
      this._schemaFeature.selectors.schema
        .asSignal()
        .tryGetParentEntityOf(entity),
  )
  /**
   * Shows dialog title based on {@link editedEntityIdSg}
   */
  public readonly titleSg = ngCore.computed(
    (modelName = this.editedEntitySg()?.name) => `Add ${modelName}`,
  )

  /**
   * Handles the change of the parent record ID.
   */
  public recordParentIdChangeHandler(value: unknown): void {
    if (ents.core.dms.data.Record.Id.isId(value)) {
      const record = _cloneDeep(this._record())
      if (record) {
        record.parentId ??= value
        this.displayFeature.actions.updateRecord
          .from(`Dialog.Form.Input-${value}.Changed`)
          .dispatch({ record })
      }
    }
  }

  /**
   * Handles the change of an attribute value.
   */
  public attributeValueChangeHandler(
    attributeId: PartialId,
    value: ents.core.dms.data.Value,
  ): void {
    const record = _cloneDeep(this._record())
    if (record) {
      record.values ??= {}
      record.values[attributeId] = value
      this.displayFeature.actions.updateRecord
        .from(`Dialog.Form.Input-${attributeId}.Changed`)
        .dispatch({ record })
    }
  }

  /**
   * Handling save click
   */
  public saveClickHandler() {
    const record = this._record()
    if (record) {
      this.displayFeature.actions.submitRecord
        .from('Dialog.Edit.Footer.Buttons.Save.Clicked')
        .dispatch({
          entityId: this.editedEntityIdSg(),
          record,
        })
    }
  }

  /**
   * Handling cancel click
   */
  public cancelBtnClickHandler() {
    this.displayFeature.actions.discardRecord
      .from(' Dialog.Edit.Footer.Buttons.Cancel.Clicked')
      .dispatch({ entityId: this.editedEntityIdSg() })
  }
}
